"use client";

import Image from "next/image";
import { Mail } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { isFeatureEnabled } from "@/lib/feature-flags";
import { MAX_EMAIL_LENGTH } from "@/lib/newsletter";

const headingFont = { fontFamily: "var(--font-display), 'Hanken Grotesk', sans-serif" };

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

export default function NewsletterSignupSection() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  // Defer feature-flag check to client-only. posthog.isFeatureEnabled is not
  // available during SSR, so evaluating it at render time produces a different
  // result on server vs client and triggers a hydration mismatch.
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedEmail = email.trim();

    if (!isValidEmail(trimmedEmail)) {
      setStatus("error");
      setMessage("Valid email is required");
      return;
    }

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmedEmail }),
      });
      const data = await response.json().catch(() => null);

      if (!response.ok || data?.success !== true) {
        throw new Error(data?.error || "Failed to subscribe");
      }

      setStatus("success");
      setMessage("Thanks. You're on the list.");
      setEmail("");
    } catch (err) {
      setStatus("error");
      setMessage(err instanceof Error ? err.message : "Failed to subscribe");
    }
  }

  if (!mounted || !isFeatureEnabled("NEWSLETTER_SIGNUP")) {
    return null;
  }

  return (
    <section className="flex w-full flex-col items-center gap-12 bg-white px-6 py-16 sm:px-10 lg:px-[150px] lg:py-20">
      <div
        data-testid="newsletter-panel"
        className="flex w-full max-w-[1140px] flex-col items-center gap-10 rounded-3xl bg-[rgba(20,100,255,0.08)] px-6 py-10 text-center sm:px-10 md:gap-12"
      >
        <Image
          src="/c2b2a125dd99cf30a4a7e99e44e341939e976761.png"
          alt="Email newsletter icon"
          width={120}
          height={120}
          className="h-[120px] w-[120px] object-contain"
        />

        <div className="flex w-full flex-col items-center gap-5">
          <h2
            className="w-full text-[32px] font-bold leading-[42px] text-[#1B1C30] sm:text-[40px] sm:leading-[52px]"
            style={headingFont}
          >
            Get Amazon Growth Tips in Your Inbox
          </h2>
          <p className="w-full text-[15px] leading-5 text-[#4D4D5C]" style={headingFont}>
            Receive expert tips, AI optimization strategies, and Amazon listing insights to help grow your sales faster.
          </p>
        </div>

        <form
          noValidate
          onSubmit={handleSubmit}
          data-testid="newsletter-field"
          className="flex w-full max-w-[460px] flex-col items-start gap-3 text-left"
        >
          <label htmlFor="newsletter-email" className="w-full text-[15px] leading-5 text-[#4D4D5C]" style={headingFont}>
            Email address
          </label>
          <div className="flex w-full flex-col gap-3 sm:flex-row">
            <div className="flex h-12 min-w-0 flex-1 items-center gap-2 rounded-lg border border-[#E0EAF4] bg-white px-4 py-2.5">
              <Mail aria-hidden="true" className="h-5 w-5 shrink-0 text-[#4D4D5C]" strokeWidth={1.5} />
              <input
                id="newsletter-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                maxLength={MAX_EMAIL_LENGTH}
                placeholder="Enter your email"
                value={email}
                onChange={(event) => {
                  setEmail(event.target.value);
                  if (status === "error" || status === "success") {
                    setStatus("idle");
                    setMessage("");
                  }
                }}
                disabled={status === "submitting"}
                className="h-5 min-w-0 flex-1 bg-transparent text-[15px] leading-5 text-[#1B1C30] outline-none placeholder:text-[#B1B0BE] disabled:cursor-not-allowed"
                style={headingFont}
              />
            </div>
            <button
              type="submit"
              disabled={status === "submitting"}
              className="h-12 shrink-0 rounded-lg bg-[#1464FF] px-6 text-[15px] font-semibold leading-5 text-white transition hover:bg-[#0F51D6] disabled:cursor-not-allowed disabled:bg-[#8CB4FF]"
              style={headingFont}
            >
              {status === "submitting" ? "Subscribing..." : "Subscribe"}
            </button>
          </div>
          {message ? (
            <p
              aria-live="polite"
              className={`text-[14px] leading-5 ${status === "success" ? "text-[#0F7A45]" : "text-[#B42318]"}`}
              style={headingFont}
            >
              {message}
            </p>
          ) : null}
        </form>
      </div>
    </section>
  );
}
